import { useCallback, useEffect, useMemo, useState } from "react";
import type { Factor } from "@supabase/supabase-js";
import { Copy, Loader2, ShieldCheck, ShieldOff, Smartphone } from "lucide-react";
import { XStack, YStack } from "tamagui";
import { toast } from "sonner";

import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import {
  AppButton,
  AppButtonRow,
  AppCard,
  AppCardBody,
  AppInlineAlert,
  AppInput,
  AppText,
  kindredFontFamilies,
} from "../../ui-foundation";

type EnrolledTotp = {
  factorId: string;
  qrCode: string;
  secret: string;
};

type MfaSettingsCardProps = {
  enforced?: boolean;
  enforcedReason?: string;
};

function ensureQrDataUrl(value: string) {
  return value.startsWith("data:") ? value : `data:image/svg+xml;utf-8,${value}`;
}

const MfaSettingsCard = ({ enforced = false, enforcedReason }: MfaSettingsCardProps) => {
  const { user, role, mfaLevel, requiresMfa, refreshSecurity } = useAuth();
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [disabling, setDisabling] = useState(false);
  const [setupCode, setSetupCode] = useState("");
  const [enrollment, setEnrollment] = useState<EnrolledTotp | null>(null);
  const [verifiedFactors, setVerifiedFactors] = useState<Factor[]>([]);
  const [unverifiedFactors, setUnverifiedFactors] = useState<Factor[]>([]);

  const loadFactors = useCallback(async () => {
    if (!user) {
      setLoading(false);
      return;
    }

    setLoading(true);
    const { data, error } = await supabase.auth.mfa.listFactors();

    if (error) {
      console.error(error);
      toast.error("Could not load two-step verification settings.");
    } else {
      setVerifiedFactors(data.totp.filter((factor) => factor.status === "verified"));
      setUnverifiedFactors(data.totp.filter((factor) => factor.status !== "verified"));
    }

    setLoading(false);
  }, [user]);

  useEffect(() => {
    void loadFactors();
  }, [loadFactors]);

  const activeFactor = verifiedFactors[0] || null;
  const hasVerifiedFactor = Boolean(activeFactor);
  const mustStayOn = enforced || role === "admin" || role === "moderator";

  const startSetup = async () => {
    if (!user) return;

    setStarting(true);

    for (const factor of unverifiedFactors) {
      await supabase.auth.mfa.unenroll({ factorId: factor.id });
    }

    const { data, error } = await supabase.auth.mfa.enroll({
      factorType: "totp",
      friendlyName: "Kindred authenticator",
    });

    if (error || !data || data.type !== "totp") {
      console.error(error);
      toast.error(error?.message || "Could not start two-step verification.");
    } else {
      setEnrollment({
        factorId: data.id,
        qrCode: ensureQrDataUrl(data.totp.qr_code),
        secret: data.totp.secret,
      });
      toast.success("Scan the QR code, then enter the 6-digit code from your authenticator app.");
    }

    setStarting(false);
  };

  const verifySetup = async () => {
    if (!enrollment) return;

    const code = setupCode.trim();
    if (!code) {
      toast.error("Enter the 6-digit code from your authenticator app.");
      return;
    }

    setVerifying(true);
    const { error } = await supabase.auth.mfa.challengeAndVerify({
      factorId: enrollment.factorId,
      code,
    });

    if (error) {
      console.error(error);
      toast.error(error.message || "Could not verify that authenticator code.");
    } else {
      setEnrollment(null);
      setSetupCode("");
      toast.success("Two-step verification is now on.");
      await Promise.all([loadFactors(), refreshSecurity()]);
    }

    setVerifying(false);
  };

  const disableMfa = async () => {
    if (!activeFactor) return;
    if (mfaLevel !== "aal2") {
      toast.error("Sign in with your authenticator code before turning two-step verification off.");
      return;
    }

    setDisabling(true);
    const { error } = await supabase.auth.mfa.unenroll({ factorId: activeFactor.id });

    if (error) {
      console.error(error);
      toast.error(error.message || "Could not turn off two-step verification.");
    } else {
      toast.success("Two-step verification is now off.");
      await Promise.all([loadFactors(), refreshSecurity()]);
    }

    setDisabling(false);
  };

  const copySecret = async () => {
    if (!enrollment?.secret) return;
    await navigator.clipboard.writeText(enrollment.secret);
    toast.success("Authenticator secret copied.");
  };

  const statusLabel = useMemo(() => {
    if (loading) return "Checking security…";
    if (mustStayOn && !hasVerifiedFactor) return "Required before privileged access is unlocked";
    if (requiresMfa) return "Second step required before Kindred access";
    if (hasVerifiedFactor) return "Two-step verification is on";
    return "Two-step verification is off";
  }, [hasVerifiedFactor, loading, mustStayOn, requiresMfa]);

  return (
    <AppCard>
      <AppCardBody gap="$sm">
        <XStack alignItems="flex-start" gap="$sm">
          <YStack
            width={40}
            height={40}
            alignItems="center"
            justifyContent="center"
            borderRadius="$pill"
            backgroundColor="$primarySoft"
          >
            <ShieldCheck size={18} className="text-primary" />
          </YStack>
          <YStack flex={1} gap={6}>
            <AppText fontFamily={kindredFontFamilies.sansBold} fontSize="$sm" lineHeight="$sm">
              Two-step verification
            </AppText>
            <AppText color="$colorMuted" fontSize="$sm" lineHeight="$md">
              Add an authenticator-app code after your password. This protects your Kindred account if your password is ever exposed.
            </AppText>
            {mustStayOn ? (
              <AppText color="$primary" fontSize="$xs" lineHeight="$sm">
                {enforcedReason || "Because this account can access admin or moderation tools, two-step verification must stay on."}
              </AppText>
            ) : null}
            <AppText color="$colorMuted" fontSize="$xs" lineHeight="$sm">
              {statusLabel}
            </AppText>
          </YStack>
        </XStack>

        {hasVerifiedFactor ? (
          <AppCard backgroundColor="$background" borderRadius="$md">
            <AppCardBody gap="$sm">
              <XStack alignItems="center" gap="$xs">
                <Smartphone size={15} className="text-primary" />
                <AppText fontFamily={kindredFontFamilies.sansBold} fontSize="$sm" lineHeight="$sm">
                  {activeFactor?.friendly_name || "Authenticator app connected"}
                </AppText>
              </XStack>
              <AppText color="$colorMuted" fontSize="$sm" lineHeight="$md">
                You will be asked for a 6-digit authenticator code after password sign-in.
              </AppText>
              {mustStayOn ? (
                <AppInlineAlert title="Required for privileged access">
                  Two-step verification is locked on for privileged accounts. Remove admin or moderator access before turning it off.
                </AppInlineAlert>
              ) : (
                <AppButton type="button" tone="outline" onPress={disableMfa} disabled={disabling} width="100%">
                  {disabling ? <Loader2 className="animate-spin" size={16} /> : <ShieldOff size={16} />}
                  {disabling ? "Turning off..." : "Turn off two-step verification"}
                </AppButton>
              )}
            </AppCardBody>
          </AppCard>
        ) : enrollment ? (
          <AppCard backgroundColor="$background" borderRadius="$md">
            <AppCardBody gap="$sm">
              <YStack gap="$xs">
                <AppText fontFamily={kindredFontFamilies.sansBold} fontSize="$sm" lineHeight="$sm">
                  1. Scan the QR code
                </AppText>
                <img
                  src={enrollment.qrCode}
                  alt="Authenticator QR code"
                  style={{
                    display: "block",
                    width: 176,
                    height: 176,
                    borderRadius: 20,
                    border: "1px solid var(--color-border)",
                    background: "white",
                    padding: 8,
                  }}
                />
              </YStack>

              <YStack gap="$xs">
                <AppText fontFamily={kindredFontFamilies.sansBold} fontSize="$sm" lineHeight="$sm">
                  2. Or enter this secret manually
                </AppText>
                <XStack gap="$sm" alignItems="center">
                  <AppInput readOnly value={enrollment.secret} fontFamily={kindredFontFamilies.mono} fontSize="$sm" flex={1} />
                  <AppButton type="button" tone="outline" onPress={copySecret} width={44} minWidth={44} paddingHorizontal={0}>
                    <Copy size={16} />
                  </AppButton>
                </XStack>
              </YStack>

              <YStack gap="$xs">
                <AppText fontFamily={kindredFontFamilies.sansBold} fontSize="$sm" lineHeight="$sm">
                  3. Enter the 6-digit code
                </AppText>
                <AppInput
                  value={setupCode}
                  onChangeText={(value) => setSetupCode(value.replace(/\D/g, "").slice(0, 6))}
                  inputMode="numeric"
                  pattern="[0-9]*"
                  placeholder="123456"
                  maxWidth={180}
                />
              </YStack>

              <AppButtonRow>
                <AppButton type="button" onPress={verifySetup} disabled={verifying} flex={1} minWidth={180}>
                  {verifying ? <Loader2 className="animate-spin" size={16} /> : <ShieldCheck size={16} />}
                  {verifying ? "Checking..." : "Verify and turn on"}
                </AppButton>
                <AppButton
                  type="button"
                  tone="outline"
                  onPress={() => {
                    setEnrollment(null);
                    setSetupCode("");
                  }}
                  flex={1}
                  minWidth={140}
                >
                  Cancel
                </AppButton>
              </AppButtonRow>
            </AppCardBody>
          </AppCard>
        ) : (
          <AppButton type="button" onPress={startSetup} disabled={starting} width="100%">
            {starting ? <Loader2 className="animate-spin" size={16} /> : <ShieldCheck size={16} />}
            {starting ? "Starting..." : "Set up with an authenticator app"}
          </AppButton>
        )}
      </AppCardBody>
    </AppCard>
  );
};

export default MfaSettingsCard;
